import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';

import PageTitle from '../../markup/Layout/PageTitle';
import Jobfindbox from '../../markup/Element/Jobfindbox';
import Accordsidebar from '../../markup/Element/Accordsidebar';
import { getAllSelectors } from '../../services/SelectorsService';
import { getPublicVacancies } from '../../services/VacanciesService';
import { LogoSpinner } from '../../components/LogoSpinner';
import { useLanguage } from '../../i18n/LanguageContext';

var bnr = require('./../../images/banner/bnr1.jpg');

// Note: date and currency formatting happen inside the component using locale

export function BrowseJobFilterGrid() {
    const [selectors, setSelectors] = useState({
        categorias: [],
        modalidadesTrabajo: [],
        tiposTrabajo: [],
        tiposContrato: [],
        tiemposExperiencia: [],
        paises: [],
        ciudades: []
    });
    const [loadingSelectors, setLoadingSelectors] = useState(true);

    const [filters, setFilters] = useState({
        query: '',
        categoriaId: '',
        modalidadTrabajoId: '',
        tipoTrabajoId: '',
        tipoContratoTrabajoId: '',
        tiempoExperienciaRequeridoId: '',
        paisId: '',
        ciudadId: '',
        salarioMinimo: '',
        salarioMaximo: '',
        inglesRequerido: null,
        tags: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    const [sorting, setSorting] = useState({
        orderBy: 'createdAt',
        orderDirection: 'desc'
    });

    const [vacancies, setVacancies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadSelectors = async () => {
            try {
                setLoadingSelectors(true);
                const response = await getAllSelectors();
                const data = response.data;
                setSelectors({
                    categorias: data.categorias || [],
                    modalidadesTrabajo: data.modalidadesTrabajo || [],
                    tiposTrabajo: data.tiposTrabajo || [],
                    tiposContrato: data.tiposContrato || [],
                    tiemposExperiencia: data.tiemposExperiencia || [],
                    paises: data.paises || [],
                    ciudades: data.ciudades || []
                });
            } catch (err) {
                setError(t('jobsection.errorLoadingSelectors'));
                console.error('Error loading selectors:', err);
            } finally {
                setLoadingSelectors(false);
            }
        };

        loadSelectors();
    }, []);

    const loadVacancies = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                orderBy: sorting.orderBy,
                orderDirection: sorting.orderDirection
            };

            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await getPublicVacancies(params);
            const data = response.data;

            setVacancies(data.data || []);
            setPagination(prev => ({
                ...prev,
                total: data.total || 0,
                totalPages: Math.ceil((data.total || 0) / pagination.limit)
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || t('jobsection.errorLoadingVacancies');
            setError(errorMessage);
            console.error('Error loading vacancies:', err);
            setVacancies([]);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.limit, sorting.orderBy, sorting.orderDirection]);

    useEffect(() => {
        if (!loadingSelectors) {
            loadVacancies();
        }
    }, [loadVacancies, loadingSelectors]);

    const handleFilterChange = (newFilters) => {
        setFilters(prev => {
            const updated = { ...prev, ...newFilters };
            if (newFilters.paisId !== undefined && newFilters.paisId !== prev.paisId) {
                updated.ciudadId = '';
            }
            return updated;
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearch = (searchFilters) => {
        setFilters(prev => ({ ...prev, ...searchFilters }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleResetFilters = () => {
        setFilters({
            query: '',
            categoriaId: '',
            modalidadTrabajoId: '',
            tipoTrabajoId: '',
            tipoContratoTrabajoId: '',
            tiempoExperienciaRequeridoId: '',
            paisId: '',
            ciudadId: '',
            salarioMinimo: '',
            salarioMaximo: '',
            inglesRequerido: null,
            tags: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleSortChange = (e) => {
        const value = e.target.value;
        if (value === 'createdAt-desc' || value === 'createdAt-asc') {
            setSorting({
                orderBy: 'createdAt',
                orderDirection: value === 'createdAt-desc' ? 'desc' : 'asc'
            });
        } else if (value === 'titulo-asc' || value === 'titulo-desc') {
            setSorting({
                orderBy: 'titulo',
                orderDirection: value === 'titulo-desc' ? 'desc' : 'asc'
            });
        }
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const getRelationName = (relation) => {
        return relation?.nombre || 'N/A';
    };

    const getPageNumbers = () => {
        const pages = [];
        const totalPages = pagination.totalPages;
        const currentPage = pagination.page;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const { t, language } = useLanguage();

    const formatRelativeDate = (dateString) => {
        if (!dateString) return t('jobsection.noDate');
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffSeconds = Math.floor((date - now) / 1000);
            const rtf = new Intl.RelativeTimeFormat(language === 'es' ? 'es' : 'en', { numeric: 'auto' });
            const divisions = [
                { amount: 60, name: 'second' },
                { amount: 60, name: 'minute' },
                { amount: 24, name: 'hour' },
                { amount: 7, name: 'day' },
                { amount: 4.34524, name: 'week' },
                { amount: 12, name: 'month' },
                { amount: Number.POSITIVE_INFINITY, name: 'year' }
            ];
            let seconds = diffSeconds;
            for (let i = 0; i < divisions.length; i++) {
                const unit = divisions[i];
                if (Math.abs(seconds) < Math.abs(unit.amount) || unit.name === 'year') {
                    const value = Math.round(seconds);
                    // convert value to unit scale
                    let displayValue = value;
                    if (i > 0) {
                        let scale = 1;
                        for (let j = 0; j < i; j++) scale *= divisions[j].amount;
                        displayValue = Math.round(seconds / scale);
                    }
                    return rtf.format(displayValue, unit.name);
                }
                seconds = seconds / unit.amount;
            }
            return t('jobsection.noDate');
        } catch (e) {
            return t('jobsection.noDate');
        }
    };

    const formatSalary = (min, max) => {
        if (!min && !max) return t('jobsection.noSalary');
        const locale = language === 'es' ? 'es-DO' : 'en-US';
        const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
        const prefixFrom = t('jobsection.salaryFrom');
        const prefixTo = t('jobsection.salaryTo');
        if (min && max) {
            return `RD$ ${nf.format(min)} - RD$ ${nf.format(max)}`;
        }
        if (min) return `${prefixFrom} ${nf.format(min)}`;
        if (max) return `${prefixTo} ${nf.format(max)}`;
        return t('jobsection.noSalary');
    };


    if (loadingSelectors) {
        return <LogoSpinner />;
    }

    return (
        <>
            <div className="page-content bg-white">
                <div className="dez-bnr-inr overlay-black-middle" style={{ backgroundImage: "url( " + bnr + ")" }}>
                    <PageTitle motherName={t('header.home')} activeName={t('jobsection.searchTitle')} />
                </div>
                <Jobfindbox
                    selectors={selectors}
                    filters={filters}
                    onSearch={handleSearch}
                />
                <div className="content-block">
                    <div className="section-full browse-job p-b50">
                        <div className="container">
                            <div className="row">
                                <Accordsidebar
                                    selectors={selectors}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    onReset={handleResetFilters}
                                />
                                <div className="col-xl-9 col-lg-8 col-md-7">
                                    {error && (
                                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="m-b20">
                                            {error}
                                        </Alert>
                                    )}

                                    <div className="job-bx-title clearfix">
                                        <h5 className="font-weight-700 pull-left text-uppercase" style={{ color: '#333' }}>
                                            {loading ? t('jobsection.loading') : (pagination.total === 1 ? `${pagination.total} ${t('jobsection.resultSingular')}` : `${pagination.total} ${t('jobsection.resultPlural')}`)}
                                        </h5>
                                        <div className="float-right">
                                            <span className="select-title">{t('jobsection.sortBy')}</span>
                                            <select className="custom-btn" value={`${sorting.orderBy}-${sorting.orderDirection}`} onChange={handleSortChange}>
                                                <option value="createdAt-desc">{t('jobsection.sortMostRecent')}</option>
                                                <option value="createdAt-asc">{t('jobsection.sortOldest')}</option>
                                                <option value="titulo-asc">{t('jobsection.sortTitleAsc')}</option>
                                                <option value="titulo-desc">{t('jobsection.sortTitleDesc')}</option>
                                            </select>
                                        </div>
                                    </div>

                                    {loading ? (
                                        <div className="text-center p-t50 p-b50">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">{t('jobsection.loadingVacancies')}</span>
                                            </div>
                                            <p className="m-t20">{t('jobsection.loadingVacancies')}</p>
                                        </div>
                                    ) : vacancies.length === 0 ? (
                                        <div className="text-center p-t50 p-b50">
                                            <p style={{ fontSize: '18px', color: '#666' }}>{t('jobsection.noVacanciesWithFilters')}</p>
                                            <p style={{ fontSize: '14px', color: '#999', marginTop: '10px' }}>{t('jobsection.tryAdjustFilters')}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <ul className="post-job-bx browse-job-grid row">
                                                {vacancies.map((vacancy) => {
                                                    const location = `${getRelationName(vacancy.ciudad)}, ${getRelationName(vacancy.pais)}`;
                                                    const modalidad = getRelationName(vacancy.modalidadTrabajo);
                                                    const timeAgo = formatRelativeDate(vacancy.createdAt);
                                                    const salary = formatSalary(vacancy.salarioMinimo, vacancy.salarioMaximo);

                                                    return (
                                                        <li className="col-lg-6 col-md-12" key={vacancy.id}>
                                                            <div className="post-bx" style={{
                                                                border: '1px solid #e8e8e8',
                                                                borderRadius: '8px',
                                                                padding: '20px',
                                                                marginBottom: '20px',
                                                                transition: 'all 0.3s ease',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                                opacity: vacancy.isExpired ? 0.7 : 1,
                                                                position: 'relative'
                                                            }}>
                                                                    {vacancy.isExpired && (
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        top: '10px',
                                                                        right: '10px',
                                                                        backgroundColor: '#dc3545',
                                                                        color: '#fff',
                                                                        padding: '4px 12px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600',
                                                                        textTransform: 'uppercase'
                                                                    }}>
                                                                        {t('jobsection.expired')}
                                                                    </div>
                                                                )}
                                                                <div className="d-flex m-b30">
                                                                    <div className="job-post-info" style={{ width: '100%' }}>
                                                                        <h5 style={{ color: '#333', marginBottom: '10px' }}>
                                                                            <Link to={`/job-detail?id=${vacancy.id}`} style={{
                                                                                color: '#333',
                                                                                textDecoration: 'none',
                                                                                transition: 'color 0.3s ease'
                                                                            }}
                                                                                onMouseEnter={(e) => e.target.style.color = '#009247'}
                                                                                onMouseLeave={(e) => e.target.style.color = '#333'}>
                                                                                {vacancy.titulo}
                                                                            </Link>
                                                                        </h5>
                                                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                                            <li style={{ marginBottom: '8px', color: '#666' }}>
                                                                                <i className="fa fa-map-marker" style={{ color: '#EFBB20', marginRight: '8px' }}></i>
                                                                                {location}
                                                                            </li>
                                                                            <li style={{ marginBottom: '8px', color: '#666' }}>
                                                                                <i className="fa fa-bookmark-o" style={{ color: '#EFBB20', marginRight: '8px' }}></i>
                                                                                {modalidad}
                                                                            </li>
                                                                            <li style={{ marginBottom: '8px', color: '#666' }}>
                                                                                <i className="fa fa-clock-o" style={{ color: '#EFBB20', marginRight: '8px' }}></i>
                                                                                {timeAgo}
                                                                            </li>
                                                                            {vacancy.categoria && (
                                                                                <li style={{ marginBottom: '8px', color: '#666' }}>
                                                                                    <i className="fa fa-briefcase" style={{ color: '#EFBB20', marginRight: '8px' }}></i>
                                                                                    {vacancy.categoria.nombre}
                                                                                </li>
                                                                            )}
                                                                        </ul>
                                                                        {vacancy.descripcion && (
                                                                            <div style={{
                                                                                marginTop: '15px',
                                                                                paddingTop: '15px',
                                                                                borderTop: '1px solid #e8e8e8'
                                                                            }}>
                                                                                <div
                                                                                    dangerouslySetInnerHTML={{ __html: vacancy.descripcion.substring(0, 200) + (vacancy.descripcion.length > 200 ? '...' : '') }}
                                                                                    style={{
                                                                                        color: '#666',
                                                                                        fontSize: '14px',
                                                                                        lineHeight: '1.6',
                                                                                        wordBreak: 'break-word'
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex">
                                                                    <div className="job-time mr-auto">
                                                                        <Link to={`/browse-job-filter-grid?modalidadTrabajoId=${vacancy.modalidadTrabajoId}`} style={{ textDecoration: 'none' }}>
                                                                            <span style={{
                                                                                background: '#f5f5f5',
                                                                                color: '#666',
                                                                                padding: '5px 15px',
                                                                                borderRadius: '20px',
                                                                                fontSize: '12px',
                                                                                fontWeight: '500',
                                                                                border: '1px solid #e8e8e8'
                                                                            }}>
                                                                                {modalidad}
                                                                            </span>
                                                                        </Link>
                                                                    </div>
                                                                    <div className="salary-bx">
                                                                        <span style={{
                                                                            color: '#333',
                                                                            fontWeight: '600',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            {salary}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <label className="like-btn" style={{ display: 'none' }}>
                                                                    <input type="checkbox" />
                                                                    <span className="checkmark"></span>
                                                                </label>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>

                                            {pagination.totalPages > 1 && (
                                                <div className="pagination-bx float-right m-t30">
                                                    <ul className="pagination">
                                                        <li className={pagination.page === 1 ? 'disabled' : 'previous'}>
                                                            <Link
                                                                to="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (pagination.page > 1) handlePageChange(pagination.page - 1);
                                                                }}
                                                                style={{
                                                                    color: pagination.page === 1 ? '#ccc' : '#666',
                                                                    textDecoration: 'none',
                                                                    transition: 'color 0.3s ease',
                                                                    pointerEvents: pagination.page === 1 ? 'none' : 'auto'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (pagination.page > 1) e.target.style.color = '#009247';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (pagination.page > 1) e.target.style.color = '#666';
                                                                }}>
                                                                <i className="ti-arrow-left"></i> {t('jobsection.previous')}
                                                            </Link>
                                                        </li>
                                                        {getPageNumbers().map((pageNum, index) => (
                                                            <li key={index} className={pageNum === pagination.page ? 'active' : ''}>
                                                                {pageNum === '...' ? (
                                                                    <span style={{ padding: '8px 12px', color: '#666' }}>...</span>
                                                                ) : (
                                                                    <Link
                                                                        to="#"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            handlePageChange(pageNum);
                                                                        }}
                                                                        style={{
                                                                            background: pageNum === pagination.page ? '#009247' : 'transparent',
                                                                            color: pageNum === pagination.page ? '#fff' : '#666',
                                                                            borderColor: pageNum === pagination.page ? '#009247' : '#e8e8e8',
                                                                            textDecoration: 'none',
                                                                            transition: 'all 0.3s ease'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            if (pageNum !== pagination.page) e.target.style.color = '#009247';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            if (pageNum !== pagination.page) e.target.style.color = '#666';
                                                                        }}>
                                                                        {pageNum}
                                                                    </Link>
                                                                )}
                                                            </li>
                                                        ))}
                                                        <li className={pagination.page === pagination.totalPages ? 'disabled' : 'next'}>
                                                            <Link
                                                                to="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (pagination.page < pagination.totalPages) handlePageChange(pagination.page + 1);
                                                                }}
                                                                style={{
                                                                    color: pagination.page === pagination.totalPages ? '#ccc' : '#666',
                                                                    textDecoration: 'none',
                                                                    transition: 'color 0.3s ease',
                                                                    pointerEvents: pagination.page === pagination.totalPages ? 'none' : 'auto'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (pagination.page < pagination.totalPages) e.target.style.color = '#009247';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (pagination.page < pagination.totalPages) e.target.style.color = '#666';
                                                                }}>
                                                                {t('jobsection.next')} <i className="ti-arrow-right"></i>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
